import { colorSystem, Text as DesignSystemText } from '@luminovo/design-system';
import { styled } from '@mui/material';
import { Pie, PieChart, ResponsiveContainer, Sector, Tooltip, useChartWidth } from 'recharts';

import { TooltipDetails } from '../../internal/GraphTooltip';
import { theme } from '../../internal/theme';
import { chartTheme } from '../chartTheme';

interface Datum {}

export type HalfDonutChartProps<T extends Datum> = {
    data: T[];
    /** Shown above the total in the middle of the arc. */
    title: string;
    onSegmentClick?: (datum: T) => void;
    getLabel: (datum: T) => string | JSX.Element;
    getValue: (datum: T) => number;
    getColor: (datum: T) => { unselected: string; selected: { border: string; background: string } };
    getTooltip?: (datum: T) => { title: JSX.Element; description: JSX.Element };
    isSelected?: (datum: T) => boolean;
};

const notSelected = () => false;

const THICKNESS = 20;
const CORNER_RADIUS = 4;
// Degrees left blank on either side of a segment, so neighbours are 2° apart and the ends sit 1° in from the baseline.
const ANGLE_GAP = 1;

type Segment<T> = { value: number; datum: T };

export function HalfDonutChart<T extends Datum>({
    data,
    title,
    onSegmentClick,
    getColor,
    getLabel,
    getValue,
    getTooltip,
    isSelected = notSelected,
}: HalfDonutChartProps<T>): JSX.Element {
    const total = data.map((datum) => getValue(datum)).reduce((a, b) => a + b, 0);
    const segments: Segment<T>[] = data.map((datum) => ({ value: getValue(datum), datum }));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 8, justifyContent: 'space-between' }}>
                {data.map((datum, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'center' }}>
                        <div
                            style={{
                                borderRadius: 2,
                                width: 8,
                                height: 8,
                                backgroundColor: getColor(datum).unselected,
                            }}
                        />
                        <DesignSystemText color={colorSystem.neutral[6]}>{getLabel(datum)}</DesignSystemText>
                        <DesignSystemText color={colorSystem.neutral[9]}>{getValue(datum)}</DesignSystemText>
                    </div>
                ))}
            </div>
            <ChartWrapper>
                <ResponsiveContainer width="100%" aspect={2}>
                    <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                        <HalfDonutArc
                            segments={segments}
                            getColor={getColor}
                            isSelected={isSelected}
                            onSegmentClick={onSegmentClick}
                        />
                        <Tooltip
                            allowEscapeViewBox={{ x: true, y: true }}
                            wrapperStyle={{ zIndex: 1 }}
                            content={({ active, payload }) => {
                                const segment = payload?.[0]?.payload as Segment<T> | undefined;
                                if (!active || !segment) {
                                    return null;
                                }
                                return (
                                    <TooltipBox>
                                        <TooltipDetails
                                            color={getColor(segment.datum).unselected}
                                            label={getLabel(segment.datum)}
                                            count={segment.value}
                                            tooltip={getTooltip?.(segment.datum)}
                                        />
                                    </TooltipBox>
                                );
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
                <Title>{title}</Title>
                <Total>{total}</Total>
            </ChartWrapper>
        </div>
    );
}

function HalfDonutArc<T extends Datum>({
    segments,
    getColor,
    isSelected,
    onSegmentClick,
}: Pick<HalfDonutChartProps<T>, 'getColor' | 'onSegmentClick'> & {
    segments: Segment<T>[];
    isSelected: (datum: T) => boolean;
}): JSX.Element {
    const outerRadius = (useChartWidth() ?? 0) / 2;

    return (
        <Pie
            data={segments}
            dataKey="value"
            cx="50%"
            cy="100%"
            startAngle={180 - ANGLE_GAP}
            endAngle={ANGLE_GAP}
            paddingAngle={2 * ANGLE_GAP}
            cornerRadius={CORNER_RADIUS}
            outerRadius={outerRadius}
            innerRadius={Math.max(outerRadius - THICKNESS, 0)}
            isAnimationActive={false}
            onClick={(_, index) => {
                const segment = segments[index];
                if (segment) {
                    onSegmentClick?.(segment.datum);
                }
            }}
            shape={(props) => {
                const segment = segments[props.index];
                if (!segment) {
                    return <Sector {...props} />;
                }
                const color = getColor(segment.datum);
                const selected = isSelected(segment.datum);
                return (
                    <Sector
                        {...props}
                        fill={selected ? color.selected.background : color.unselected}
                        stroke={selected ? color.selected.border : 'none'}
                        strokeWidth={selected ? 1 : 0}
                        strokeLinejoin="round"
                    />
                );
            }}
        />
    );
}

const ChartWrapper = styled('div')({
    position: 'relative',
    width: '100%',
    '& .recharts-surface': {
        overflow: 'visible',
    },
    '& .recharts-pie-sector': {
        ...theme.hoverable,
    },
    // Recharts makes each sector a `<g tabindex="-1">`, so a click focuses it and the browser rings it. The keyboard
    // never reaches the sectors, and a click already shows the selected colours.
    '& .recharts-pie-sector:focus, & .recharts-sector:focus': {
        outline: 'none',
    },
});

const Title = styled('div')({
    position: 'absolute',
    bottom: '50%',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontFamily: theme.fontFamily,
    fontSize: '14px',
    color: colorSystem.neutral[6],
    pointerEvents: 'none',
});

const Total = styled('div')({
    position: 'absolute',
    top: 'calc(50% + 20px)',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontFamily: theme.fontFamily,
    fontSize: '44px',
    fontWeight: 'bold',
    lineHeight: 1,
    color: colorSystem.neutral[8],
    pointerEvents: 'none',
});

const TooltipBox = styled('div')({
    background: colorSystem.neutral.white,
    ...chartTheme.tooltip,
});
